import type { BulkUpdateResponse } from '~redux/modules/superreports.types';

interface DeleteAutosheetProposalHoursParams {
  /** TIC proposal UUID, matched against the hours' `autosheet_proposal_id` column. */
  proposalId: string;
  /** Hour ids from the proposal's accepted log entry — the legacy fallback payload. */
  expectedHourIds: number[];
  deleteByProposalId: (proposalId: string) => Promise<BulkUpdateResponse>;
  bulkDelete: (hourIds: number[]) => Promise<BulkUpdateResponse>;
}

interface DeleteAutosheetProposalHoursResult {
  /** Ids actually deleted by whichever path ran. */
  deletedIds: number[];
  /** True when the legacy logged-id delete ran because the server filter matched nothing. */
  usedFallback: boolean;
}

/**
 * Delete every AutoSheet hour for a proposal, server-first with a legacy fallback.
 *
 * The server filter (`delete_by_autosheet_proposal_id`) is authoritative for hours
 * stamped after PR #6869. Proposals accepted before stamping have a NULL column, so
 * the filter matches nothing — only then do we fall back to deleting the logged ids.
 * Dependencies are injected so the decision logic is unit-tested without mocks.
 */
export async function deleteAutosheetProposalHours({
  proposalId,
  expectedHourIds,
  deleteByProposalId,
  bulkDelete,
}: DeleteAutosheetProposalHoursParams): Promise<DeleteAutosheetProposalHoursResult> {
  const serverResponse = await deleteByProposalId(proposalId);

  // Server matched rows (post-stamping), or there's nothing logged to fall back to.
  if (serverResponse.deleted_ids.length > 0 || expectedHourIds.length === 0) {
    return { deletedIds: serverResponse.deleted_ids, usedFallback: false };
  }

  // Pre-stamping proposal: NULL column, so the filter matched nothing. Delete the
  // ids recorded in the accepted log entry instead.
  const fallbackResponse = await bulkDelete(expectedHourIds);
  return { deletedIds: fallbackResponse.deleted_ids, usedFallback: true };
}

/**
 * Partial revert = strictly fewer hours deleted than the accepted log recorded. The
 * server filter is authoritative and may delete MORE than the log when it was incomplete;
 * that is a full success, not a partial, so the test is `<`, never `!==`.
 */
export function isPartialRevert(deletedCount: number, expectedCount: number): boolean {
  return expectedCount > 0 && deletedCount < expectedCount;
}
